import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FormDrawer } from "@/components/ds/FormDrawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tip } from "@/components/ds/Tip"
import { Building, Calendar, Loader2, Pencil, Plus, Search, Trash2, Users } from "@/components/ds/icons";
import { PageHeader } from "@/components/ui/PageHeader";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatNombreArea } from "@/lib/area";
import { useUser } from "@/contexts/UserContext";
import { useSetupStatusContext } from "@/contexts/SetupStatusContext";
import type { DriveStep } from "driver.js";
import { useGuide } from "@/hooks/useGuide";
import { useRegisterPageGuide } from "@/contexts/ActiveGuideContext";
import { useGuideAutostart } from "@/hooks/useGuideAutostart";
import { usePendingGuide } from "@/hooks/usePendingGuide";
import { ContratoFormDrawer } from "./ContratoFormDrawer";
import type { Contrato } from "./contratos-types";

export function Contratos() {
  const { user } = useUser();
  const tieneMandantes = user?.empresa?.tiene_mandantes ?? false;

  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Contrato | null>(null);
  const [aEliminar, setAEliminar] = useState<Contrato | null>(null);
  const [eliminando, setEliminando] = useState(false);

  // --- Guía de Contratos ---
  const { invalidate } = useSetupStatusContext();

  /**
   * guideInDialog: true mientras la guía está activa con el diálogo abierto.
   * Se usa para pasar modal={false} al FormDrawer durante esos pasos,
   * evitando que el focus trap de Radix bloquee los botones Siguiente/Anterior
   * del popover de driver.js (que se renderizan fuera del DOM del diálogo).
   *
   * En el flujo normal (sin guía) el diálogo sigue siendo modal={true} por defecto.
   */
  const [guideInDialog, setGuideInDialog] = useState(false);

  /**
   * Ref para cancelar el rAF de polling del primer campo del diálogo.
   * Se necesita para no tener efectos secundarios si el componente desmonta.
   */
  const dialogPollRafRef = useRef<number>(0);

  /**
   * Fábrica de pasos lazy: se invoca en startGuide() para capturar el estado
   * vivo de setCreateOpen y setGuideInDialog (estables entre renders).
   *
   * Paso 2 — Highlight del botón Nuevo contrato:
   *   - onHighlightStarted: agrega driver-fc-dialog-step al body → driver z-index
   *     baja a 40/45 (bajo Radix z-50), el botón queda resaltado sin tapar nada.
   *   - onDeselected: remueve driver-fc-dialog-step → driver vuelve a su z default (~10000).
   *   - onNextClick: abre el diálogo, activa modal={false} en el dialog, hace poll
   *     rAF para esperar que monte [data-tour="contrato-mandante"], luego avanza.
   *
   * Pasos 3–5 — Campos dentro del diálogo:
   *   - driver está en z default (~10000), por encima del Radix Dialog (z-50).
   *   - driver-fc-dialog-step NO está en el body → overlay de driver es visible y
   *     el cutout expone el campo seleccionado.
   *   - Radix Dialog tiene modal={false} → focus trap desactivado →
   *     botones Next/Prev de driver son clickeables.
   *
   * Paso 6 — Botón Guardar (aún in-dialog): al avanzar, desactiva guideInDialog.
   * Paso 7 — Popover de cierre (sin elemento anclado).
   *
   * NOTA: [data-tour="contrato-mandante"] es el primer campo del formulario
   * principal (el Select de mandante). Existe en el DOM desde el momento en
   * que el form renderiza (no depende de carga async), por lo que es seguro
   * como objetivo del rAF poll.
   */
  const buildContratosGuideSteps = useCallback((): DriveStep[] => [
    // Paso 1 — Popover centrado de apertura (sin elemento anclado)
    {
      popover: {
        title: "¿Para qué sirve un contrato?",
        description:
          "Un contrato es el contexto operativo (Faena, Proyecto, Servicio) entre tu empresa y un mandante o contratista. Sin un contrato real no podrás asociar documentos de seguridad a una faena. Creemos el primero.",
      },
    },
    // Paso 2 — Highlight del botón Nuevo contrato (pre-diálogo)
    // driver-fc-dialog-step body class está activa → driver z-index bajo (40/45) bajo Radix
    {
      element: '[data-tour="contratos-crear"]',
      onHighlightStarted: () => {
        document.body.classList.add("driver-fc-dialog-step");
      },
      onDeselected: () => {
        // Removemos la clase antes de los pasos in-dialog para que driver
        // vuelva a su z default (~10000) y pueda resaltar los campos dentro del diálogo.
        document.body.classList.remove("driver-fc-dialog-step");
      },
      popover: {
        title: "Crear un contrato",
        description:
          "Acá registras un contrato con uno de tus mandantes. Haz clic en «Siguiente» para abrir el formulario.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Abre el diálogo y desactiva el focus trap para que driver mantenga
          // el control. El poll rAF espera a que monte el primer campo antes
          // de avanzar, evitando que driver intente resaltar un nodo inexistente.
          setCreateOpen(true);
          setGuideInDialog(true);

          cancelAnimationFrame(dialogPollRafRef.current);
          const deadline = performance.now() + 3000;
          const tick = () => {
            if (document.querySelector('[data-tour="contrato-mandante"]')) {
              driverObj.moveNext();
              return;
            }
            if (performance.now() >= deadline) {
              // Fallback: avanzamos igual para no dejar la guía atascada.
              driverObj.moveNext();
              return;
            }
            dialogPollRafRef.current = requestAnimationFrame(tick);
          };
          dialogPollRafRef.current = requestAnimationFrame(tick);
        },
      },
    },
    // Paso 3 — Campo Mandante (primer campo, rAF poll target)
    {
      element: '[data-tour="contrato-mandante"]',
      popover: {
        title: "Mandante del contrato",
        description:
          "Selecciona la empresa mandante con la que celebras este contrato. Solo aparecen las relaciones activas.",
      },
    },
    // Paso 4 — Campo Tipo (Faena / Proyecto / Servicio / Otro)
    {
      element: '[data-tour="contrato-tipo"]',
      popover: {
        title: "Tipo de contrato",
        description:
          "Elige la naturaleza del vínculo: Faena (obra física), Proyecto, Servicio continuo u Otro. Este dato clasifica los documentos de prevención que se asociarán.",
      },
    },
    // Paso 5 — Campo Áreas
    {
      element: '[data-tour="contrato-areas"]',
      popover: {
        title: "Áreas del contrato",
        description:
          "Asocia el contrato a las áreas donde opera. Esto permite filtrar recursos y documentos por área de trabajo.",
      },
    },
    // Paso 6 — Botón Guardar (dentro del diálogo)
    {
      element: '[data-tour="contrato-guardar"]',
      popover: {
        title: "Guardar contrato",
        description:
          "Cuando completes los datos, guarda para registrar el contrato.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Al salir de los pasos in-dialog, volvemos al comportamiento modal normal.
          setGuideInDialog(false);
          driverObj.moveNext();
        },
      },
    },
    // Paso 7 — Popover de cierre/confirmación (sin elemento anclado)
    {
      popover: {
        title: "¡Listo!",
        description:
          "Puedes reabrir esta guía cuando quieras haciendo clic en «Ver guía» en el panel de primeros pasos, o en el botón de ayuda de esta página.",
      },
    },
  ], [setCreateOpen, setGuideInDialog]);

  const { startGuide } = useGuide("guide-contratos", { buildSteps: buildContratosGuideSteps });
  useRegisterPageGuide(startGuide);
  usePendingGuide("guide-contratos", startGuide, { anchorSelector: '[data-tour="contratos-crear"]' });
  useGuideAutostart("guide-contratos", startGuide, { anchorSelector: '[data-tour="contratos-crear"]' });

  // Limpieza del rAF de polling al desmontar
  useEffect(() => {
    return () => cancelAnimationFrame(dialogPollRafRef.current);
  }, []);
  // --- fin Guía de Contratos ---

  const cargar = async () => {
    setIsLoading(true);
    try {
      const res = await api.get<{ data: Contrato[] }>("/comercial/contratos");
      setContratos(res.data);
    } catch {
      toast.error("No se pudo cargar el listado de contratos.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const eliminar = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await api.delete(`/comercial/contratos/${aEliminar.id}`);
      toast.success("Contrato eliminado.");
      setAEliminar(null);
      cargar();
    } catch {
      toast.error("No se pudo eliminar el contrato.");
    } finally {
      setEliminando(false);
    }
  };

  const filtered = useMemo(() => {
    const visible = tieneMandantes ? contratos : contratos.filter((c) => !c.es_base);
    const q = search.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter(
      (c) =>
        c.nombre_referencia.toLowerCase().includes(q) ||
        c.relacion_empresa.mandante.nombre.toLowerCase().includes(q),
    );
  }, [contratos, search, tieneMandantes]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mis contratos"
        subtitle="La faena, proyecto o servicio que acuerdas con cada mandante."
        rightContent={
          <div className="flex items-center gap-2">
            {tieneMandantes && (
              <Button data-tour="contratos-crear" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Nuevo contrato
              </Button>
            )}
          </div>
        }
      />
      {/*
        modal={false} desactiva el focus trap de Radix Dialog durante los pasos
        in-dialog de la guía. Esto permite que los botones Siguiente/Anterior de
        driver.js (renderizados fuera del DOM del diálogo) sean clickeables.
        En el flujo normal sin guía, guideInDialog=false y el diálogo es modal.
      */}
      <FormDrawer open={createOpen} onOpenChange={(open) => {
        if (!open) setGuideInDialog(false);
        setCreateOpen(open);
      }} modal={guideInDialog ? false : undefined} dismissible={guideInDialog ? false : undefined}>
        <ContratoFormDrawer
          key={createOpen ? "create-open" : "create-closed"}
          mode="create"
          onSaved={() => {
            setCreateOpen(false);
            setGuideInDialog(false);
            cargar();
            invalidate();
          }}
        />
      </FormDrawer>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Contratos vigentes</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o mandante..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {contratos.length === 0
                ? "No se encontraron contratos. Si acabas de crear tu cuenta, recarga la página."
                : "No hay resultados para esa búsqueda."}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filtered.map((c) => (
                <Card key={c.id}>
                  <CardHeader>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 space-y-1">
                        <CardTitle className="text-lg flex flex-wrap items-center gap-2">
                          {c.nombre_referencia}
                          {c.es_base && (
                            <Badge variant="outline" className="shrink-0">
                              Operación interna
                            </Badge>
                          )}
                          {!c.es_base && c.relacion_empresa.estado === "pendiente" && (
                            <Badge variant="secondary" className="shrink-0">
                              Pendiente aceptación
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 truncate">
                          <Building className="h-4 w-4 shrink-0" />
                          {c.es_base ? "Operación interna" : c.relacion_empresa.mandante.nombre}
                        </CardDescription>
                      </div>
                      <Badge variant="secondary" className="shrink-0">
                        {c.tipo_contrato}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Inicio</p>
                        <p className="flex items-center gap-1 font-medium">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(c.fecha_inicio)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Término</p>
                        <p className="flex items-center gap-1 font-medium">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(c.fecha_termino)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Personas</p>
                        <p className="flex items-center gap-1 font-medium">
                          <Users className="h-3.5 w-3.5" />
                          {c.personas_count ?? 0}
                        </p>
                      </div>
                    </div>
                    {c.areas.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {c.areas.map((a) => (
                          <Badge key={a.id} variant="outline">
                            {formatNombreArea(a)}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button variant="outline" size="sm" onClick={() => setEditing(c)}>
                        <Pencil className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                      {c.es_base ? (
                        <Tip texto="El contrato de Operación interna no se puede eliminar.">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            disabled
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Eliminar
                          </Button>
                        </Tip>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setAEliminar(c)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Eliminar
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <FormDrawer open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        {editing && (
          <ContratoFormDrawer
            key={`edit-${editing.id}`}
            mode="edit"
            contrato={editing}
            onSaved={() => {
              setEditing(null);
              cargar();
            }}
          />
        )}
      </FormDrawer>

      <AlertDialog
        open={aEliminar !== null}
        onOpenChange={(open) => !open && setAEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar contrato</AlertDialogTitle>
            <AlertDialogDescription>
              {aEliminar ? (
                <>
                  ¿Seguro que quieres eliminar el contrato{" "}
                  <strong>{aEliminar.nombre_referencia}</strong>? Esta acción no se puede
                  deshacer.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                eliminar();
              }}
              disabled={eliminando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const [y, m, d] = value.split("-");
  return `${d}-${m}-${y}`;
}
